import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject,
  OnInit,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import {
  combineLatest,
  debounceTime,
  map,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { MultiselectComponent } from '@ui-lib/components';
import { PoolDictionaryService } from './pool-dictionary.service';

@Component({
  selector: 'mon-pool-selector',
  standalone: true,
  imports: [TranslateModule, FormsModule, AsyncPipe, MultiselectComponent, ReactiveFormsModule],
  templateUrl: './pool-selector.component.html',
  styleUrl: './pool-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PoolDictionaryService],
})
export class PoolSelectorComponent implements OnInit {
  protected readonly poolDictionaryService = inject(PoolDictionaryService);
  private readonly destroyRef = inject(DestroyRef);

  @Input()
  public controlEntity: FormControl<string[]> = new FormControl([], { nonNullable: true });

  @Output()
  public readonly selectionChange: EventEmitter<IDictionaryItem<string>[]> = new EventEmitter<
    IDictionaryItem<string>[]
  >();

  private loadedCount = 0;

  private readonly keyword$ = new Subject<string>();

  protected readonly pools$ = this.keyword$.pipe(
    debounceTime(500),
    switchMap(() =>
      this.poolDictionaryService.source$.pipe(
        scan(
          (
            acc: IDictionaryItem<string>[],
            newbies: IDictionaryItem<string>[],
          ): IDictionaryItem<string>[] => [...acc, ...newbies],
          [],
        ),
        map((items: IDictionaryItem<string>[]): IDictionaryItem<string>[] => {
          const seenIds = new Set<string>();

          return items.filter((item: IDictionaryItem<string>): boolean => {
            if (!item.id) return false;

            if (seenIds.has(item.id)) {
              return false;
            }

            seenIds.add(item.id);

            return true;
          });
        }),
        tap((items: IDictionaryItem<string>[]): void => {
          this.loadedCount = items.length;
        }),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public ngOnInit(): void {
    combineLatest([
      this.pools$,
      this.controlEntity.valueChanges.pipe(startWith(this.controlEntity.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([items, selectedIds]) => {
        const selectedSet = new Set(selectedIds ?? []);
        const selectedItems = items.filter(
          (item) => item.id !== undefined && selectedSet.has(item.id),
        );

        this.selectionChange.emit(selectedItems);
      });
  }

  public loadMore(): void {
    if (this.loadedCount < this.poolDictionaryService.totalCount()) {
      this.poolDictionaryService.loadMore();
    }
  }

  public onKeywordChange(keyword: string): void {
    this.poolDictionaryService.updateKeyword(keyword);
    this.keyword$.next(keyword);
  }
}
